import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import adminService, {
  type DashboardStats,
  type UsageMetrics,
  type PlatformHealth,
  type AdminPlan,
  type AdminCreatePlanRequest,
  type AdminUser,
  type AdminCreateRequest,
  type AdminUpdateRequest,
} from "@/services/adminService";

// Platform User interface (for managing users)
interface PlatformUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  subscriptionPlan: string;
  subscriptionStatus: "active" | "inactive" | "pending";
  subscriptionEndDate?: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  customerId?: string;
  subscriptionId?: string;
}

// Admin state interface
interface AdminState {
  // Authentication
  isAuthenticated: boolean;
  adminUser: AdminUser | null;
  authLoading: boolean;
  authError: string | null;

  // Administrators management
  admins: AdminUser[];
  adminsLoading: boolean;
  adminsError: string | null;
  selectedAdminForEdit: AdminUser | null;

  // Plans
  plans: AdminPlan[];
  plansLoading: boolean;
  plansError: string | null;
  selectedPlan: AdminPlan | null;

  // Users
  users: PlatformUser[];
  usersLoading: boolean;
  usersError: string | null;

  // Dashboard
  dashboardStats: DashboardStats | null;
  usageMetrics: UsageMetrics | null;
  dashboardLoading: boolean;
  dashboardError: string | null;

  // System Health
  systemHealth: PlatformHealth | null;
  healthLoading: boolean;
  healthError: string | null;

  // UI State
  sidebarCollapsed: boolean;
  currentView: "list" | "grid";
  searchQuery: string;
}

// Initial state
const initialState: AdminState = {
  // Authentication
  isAuthenticated: false,
  adminUser: null,
  authLoading: false,
  authError: null,

  // Administrators management
  admins: [],
  adminsLoading: false,
  adminsError: null,
  selectedAdminForEdit: null,

  // Plans
  plans: [],
  plansLoading: false,
  plansError: null,
  selectedPlan: null,

  // Users
  users: [],
  usersLoading: false,
  usersError: null,

  // Dashboard
  dashboardStats: null,
  usageMetrics: null,
  dashboardLoading: false,
  dashboardError: null,

  // System Health
  systemHealth: null,
  healthLoading: false,
  healthError: null,

  // UI State
  sidebarCollapsed: false,
  currentView: "grid",
  searchQuery: "",
};

export const fetchDashboardStats = createAsyncThunk(
  "admin/fetchDashboardStats",
  async (_, { rejectWithValue }) => {
    try {
      const stats = await adminService.getDashboardStats();
      return stats;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la récupération des statistiques";
      return rejectWithValue(message);
    }
  },
);

export const fetchUsageMetrics = createAsyncThunk(
  "admin/fetchUsageMetrics",
  async (timeRange: string = "30d", { rejectWithValue }) => {
    try {
      const metrics = await adminService.getUsageMetrics(timeRange);
      return metrics;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la récupération des métriques";
      return rejectWithValue(message);
    }
  },
);

export const fetchUsers = createAsyncThunk(
  "admin/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const users = await adminService.getUsers();
      return users;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la récupération des utilisateurs";
      return rejectWithValue(message);
    }
  },
);

export const deleteUser = createAsyncThunk(
  "admin/deleteUser",
  async (userId: string, { rejectWithValue }) => {
    try {
      const result = await adminService.deleteUser(userId);
      return { userId, ...result };
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la suppression de l'utilisateur";
      return rejectWithValue(message);
    }
  },
);

export const fetchSystemHealth = createAsyncThunk(
  "admin/fetchSystemHealth",
  async (_, { rejectWithValue }) => {
    try {
      const health = await adminService.getSystemHealth();
      return health;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la vérification de la santé du système";
      return rejectWithValue(message);
    }
  },
);

// Plan management async thunks
export const fetchPlans = createAsyncThunk(
  "admin/fetchPlans",
  async (_, { rejectWithValue }) => {
    try {
      const plans = await adminService.listPlans();
      return plans;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la récupération des plans";
      return rejectWithValue(message);
    }
  },
);

export const createPlan = createAsyncThunk(
  "admin/createPlan",
  async (planData: AdminCreatePlanRequest, { rejectWithValue }) => {
    try {
      const newPlan = await adminService.createPlan(planData);
      return newPlan;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la création du plan";
      return rejectWithValue(message);
    }
  },
);

export const updatePlan = createAsyncThunk(
  "admin/updatePlan",
  async (
    { id, data }: { id: string; data: Partial<AdminCreatePlanRequest> },
    { rejectWithValue },
  ) => {
    try {
      const updatedPlan = await adminService.updatePlan(id, data);
      return updatedPlan;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la mise à jour du plan";
      return rejectWithValue(message);
    }
  },
);

export const deletePlan = createAsyncThunk(
  "admin/deletePlan",
  async (id: string, { rejectWithValue }) => {
    try {
      await adminService.deletePlan(id);
      return id;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la suppression du plan";
      return rejectWithValue(message);
    }
  },
);

// Async thunks for admins management
export const fetchAdmins = createAsyncThunk(
  "admin/fetchAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const admins = await adminService.listAdmins();
      return admins;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la récupération des administrateurs";
      return rejectWithValue(message);
    }
  },
);

export const createAdmin = createAsyncThunk(
  "admin/createAdmin",
  async (adminData: AdminCreateRequest, { rejectWithValue }) => {
    try {
      const newAdmin = await adminService.createAdmin(adminData);
      return newAdmin;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la création de l'administrateur";
      return rejectWithValue(message);
    }
  },
);

export const updateAdmin = createAsyncThunk(
  "admin/updateAdmin",
  async (
    { id, data }: { id: string; data: AdminUpdateRequest },
    { rejectWithValue },
  ) => {
    try {
      const updatedAdmin = await adminService.updateAdmin(id, data);
      return updatedAdmin;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la mise à jour de l'administrateur";
      return rejectWithValue(message);
    }
  },
);

export const deleteAdmin = createAsyncThunk(
  "admin/deleteAdmin",
  async (id: string, { rejectWithValue }) => {
    try {
      await adminService.deleteAdmin(id);
      return id;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la suppression de l'administrateur";
      return rejectWithValue(message);
    }
  },
);

// Admin slice
const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    // Authentication actions
    loginStart: (state) => {
      state.authLoading = true;
      state.authError = null;
    },
    loginSuccess: (state, action: PayloadAction<AdminUser>) => {
      state.isAuthenticated = true;
      state.adminUser = action.payload;
      state.authLoading = false;
      state.authError = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.adminUser = null;
      state.authLoading = false;
      state.authError = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.adminUser = null;
      state.authError = null;
      // Reset other state
      state.admins = [];
      state.dashboardStats = null;
      state.usageMetrics = null;
    },

    // UI actions
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setView: (state, action: PayloadAction<"list" | "grid">) => {
      state.currentView = action.payload;
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    setSelectedAdminForEdit: (
      state,
      action: PayloadAction<AdminUser | null>,
    ) => {
      state.selectedAdminForEdit = action.payload;
    },

    // Clear errors
    clearErrors: (state) => {
      state.authError = null;
      state.adminsError = null;
      state.dashboardError = null;
      state.usersError = null;
      state.healthError = null;
    },
  },
  extraReducers: (builder) => {
    // Dashboard stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardStats = action.payload;
        state.dashboardError = null;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload as string;
      });

    // Usage metrics
    builder.addCase(fetchUsageMetrics.fulfilled, (state, action) => {
      state.usageMetrics = action.payload;
    });

    // Fetch users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload;
        state.usersError = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload as string;
      });

    // Delete user
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      state.users = state.users.filter((u) => u.id !== action.payload.userId);
    });

    // System health
    builder
      .addCase(fetchSystemHealth.pending, (state) => {
        state.healthLoading = true;
        state.healthError = null;
      })
      .addCase(fetchSystemHealth.fulfilled, (state, action) => {
        state.healthLoading = false;
        state.systemHealth = action.payload;
        state.healthError = null;
      })
      .addCase(fetchSystemHealth.rejected, (state, action) => {
        state.healthLoading = false;
        state.healthError = action.payload as string;
      });

    // Fetch plans
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.plansLoading = true;
        state.plansError = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.plans = action.payload;
        state.plansError = null;
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.plansLoading = false;
        state.plansError = action.payload as string;
      });

    // Create plan
    builder
      .addCase(createPlan.pending, (state) => {
        state.plansLoading = true;
        state.plansError = null;
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.plans = [action.payload, ...state.plans];
        state.plansError = null;
      })
      .addCase(createPlan.rejected, (state, action) => {
        state.plansLoading = false;
        state.plansError = action.payload as string;
      });

    // Update plan
    builder.addCase(updatePlan.fulfilled, (state, action) => {
      const index = state.plans.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.plans[index] = action.payload;
      }
      if (state.selectedPlan?.id === action.payload.id) {
        state.selectedPlan = action.payload;
      }
    });

    // Delete plan
    builder.addCase(deletePlan.fulfilled, (state, action) => {
      state.plans = state.plans.filter((p) => p.id !== action.payload);
      if (state.selectedPlan?.id === action.payload) {
        state.selectedPlan = null;
      }
    });

    // Fetch admins
    builder
      .addCase(fetchAdmins.pending, (state) => {
        state.adminsLoading = true;
        state.adminsError = null;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.adminsLoading = false;
        state.admins = action.payload;
        state.adminsError = null;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.adminsLoading = false;
        state.adminsError = action.payload as string;
      });

    // Create admin
    builder
      .addCase(createAdmin.pending, (state) => {
        state.adminsLoading = true;
        state.adminsError = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.adminsLoading = false;
        state.admins = [action.payload, ...state.admins];
        state.adminsError = null;
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.adminsLoading = false;
        state.adminsError = action.payload as string;
      });

    // Update admin
    builder.addCase(updateAdmin.fulfilled, (state, action) => {
      const index = state.admins.findIndex((a) => a.id === action.payload.id);
      if (index !== -1) {
        state.admins[index] = action.payload;
      }
      if (state.selectedAdminForEdit?.id === action.payload.id) {
        state.selectedAdminForEdit = action.payload;
      }
      // Update current admin user if it's the same
      if (state.adminUser?.id === action.payload.id) {
        state.adminUser = action.payload;
      }
    });

    // Delete admin
    builder.addCase(deleteAdmin.fulfilled, (state, action) => {
      state.admins = state.admins.filter((a) => a.id !== action.payload);
      if (state.selectedAdminForEdit?.id === action.payload) {
        state.selectedAdminForEdit = null;
      }
    });
  },
});

// Export actions
export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  toggleSidebar,
  setView,
  setSearchQuery,
  setSelectedAdminForEdit,
  clearErrors,
} = adminSlice.actions;

// Export reducer
export default adminSlice.reducer;

// Export types
export type { AdminState, AdminUser, PlatformUser };
